import React, { useEffect, useMemo, useRef, useState } from 'react';
import MemberFamilyDialog from './MemberFamilyDialog';
import type { DashboardFamilyMember, DashboardOwnMember } from './types';
import { submitFamilyChangeRequest } from './familyChangeClient';

interface RequestRemovalDialogProps {
  own: DashboardOwnMember;
  /** The family member being asked to leave. */
  member: DashboardFamilyMember;
  /** Every other active family member (not including `own` or `member`). */
  otherFamilyMembers: DashboardFamilyMember[];
  onClose: () => void;
  onDone: (message: string) => void;
}

/**
 * "Request removal" (R10, design §7.4): the primary asks for `member` to
 * leave the family. If the primary is removing themselves, a new primary
 * must be chosen from the remaining adults — if none remain, the dialog
 * says the committee will contact them. Nothing changes until an admin
 * approves it.
 */
const RequestRemovalDialog: React.FC<RequestRemovalDialogProps> = ({
  own,
  member,
  otherFamilyMembers,
  onClose,
  onDone,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const removingSelf = member.id === own.id;

  // Remaining adults once `member` has left: every other family member
  // (own included, unless own is the one leaving) who is not a junior.
  const remainingAdults = useMemo(() => {
    const candidates: DashboardFamilyMember[] = removingSelf
      ? otherFamilyMembers
      : [
          { id: own.id, firstName: own.firstName, lastName: own.lastName, isJunior: own.isJunior } as DashboardFamilyMember,
          ...otherFamilyMembers.filter((m) => m.id !== member.id),
        ];
    return candidates.filter((m) => m.id !== member.id && m.isJunior !== true);
  }, [otherFamilyMembers, own, member, removingSelf]);

  const [newPrimaryMemberId, setNewPrimaryMemberId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const needsNewPrimary = removingSelf && remainingAdults.length > 0;
  const canConfirm = !needsNewPrimary || !!newPrimaryMemberId;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const payload: Record<string, unknown> = { removeMemberId: member.id };
      if (newPrimaryMemberId) payload.newPrimaryMemberId = newPrimaryMemberId;
      const result = await submitFamilyChangeRequest('REMOVE_MEMBER', payload);
      if (result.success) {
        onDone(result.message || 'Removal request submitted. Nothing changes until an admin approves.');
      } else {
        setServerError(result.message || 'Could not submit this removal request. Please try again.');
      }
    } catch (err: any) {
      setServerError(err?.message || 'Could not submit this removal request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MemberFamilyDialog
      role="alertdialog"
      titleId="rfr-title"
      title="Request removal?"
      descriptionId="rfr-desc"
      busy={submitting}
      onClose={onClose}
    >
      <div id="rfr-desc" className="mt-2 space-y-2 text-sm text-gray-700">
        <p>
          Request that <strong>{member.firstName} {member.lastName}</strong> leaves the family?
        </p>
        <p className="rounded-md border border-amber-300 bg-amber-50 p-2 font-medium text-amber-900">
          Once approved, they keep their membership as an individual for the rest of the year.
          Nothing is refunded. An admin needs to approve this before anything changes.
        </p>
      </div>

      {removingSelf && (
        <div className="mt-3">
          {remainingAdults.length > 0 ? (
            <>
              <label htmlFor="rfr-new-primary" className="block text-sm font-medium text-gray-700 mb-1">
                Choose the new primary *
              </label>
              <select
                id="rfr-new-primary"
                value={newPrimaryMemberId || ''}
                onChange={(e) => setNewPrimaryMemberId(e.target.value || null)}
                className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
              >
                <option value="">Choose the new primary</option>
                {remainingAdults.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.firstName} {m.lastName}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <p className="rounded-md border border-gray-200 bg-gray-50 p-2 text-sm text-gray-700">
              There is no other adult in the family. The committee will contact you.
            </p>
          )}
        </div>
      )}

      {serverError && (
        <p role="alert" className="mt-3 text-sm font-medium text-red-700">
          {serverError}
        </p>
      )}

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          ref={cancelRef}
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          Keep in family
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || !canConfirm}
          className="min-h-[44px] rounded-lg bg-red-700 px-4 py-2 font-semibold text-white hover:bg-red-800 disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Request removal'}
        </button>
      </div>
    </MemberFamilyDialog>
  );
};

export default RequestRemovalDialog;
